import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Eye, Share2, MessageCircle, Download, Link2, TrendingUp } from "lucide-react";
import { VehicleProfitDialog } from "@/components/vehicleProfit";
import { FleetRanking } from "@/components/fleetRanking";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, daysUntil, openFile } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InventoryTab } from "@/components/inventory";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, SearchBox, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { FilePick } from "@/pages/Drivers";
import { DocUpload } from "@/components/market";
import { DocHistory } from "@/components/docs";

const EMPTY = { vehicle_number: "", vehicle_type: "", capacity_tons: 0, rc_number: "", rc_expiry: "", insurance_expiry: "", fitness_expiry: "", puc_number: "", puc_expiry: "", permit_expiry: "", insurance_number: "", fitness_number: "", permit_number: "", rc_file_id: null, insurance_file_id: null, fitness_file_id: null, puc_file_id: null, permit_file_id: null, status: "Available", expense_model: "actual", per_km_rate: 0 };
const STATUSES = ["Available", "On Trip", "Maintenance"];
const DOCS = [
  ["rc_file_id", "rc_expiry", "RC (Registration Certificate)"],
  ["insurance_file_id", "insurance_expiry", "Insurance Certificate"],
  ["fitness_file_id", "fitness_expiry", "Fitness Certificate"],
  ["puc_file_id", "puc_expiry", "PUC Certificate"],
  ["permit_file_id", "permit_expiry", "Permit (National / State)"],
];

function Expiry({ date }) {
  const n = daysUntil(date);
  const cls = n === null ? "" : n < 0 ? "text-rose-600 font-semibold" : n <= 30 ? "text-amber-600 font-semibold" : "";
  return <span className={cls}>{fmtDate(date)}</span>;
}

function PucBadge({ v }) {
  const n = daysUntil(v.puc_expiry);
  if (n === null) return <span className="text-muted-foreground">-</span>;
  const badge = n < 0
    ? "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-400"
    : n <= 15
      ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-400"
      : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400";
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${badge}`} data-testid={`puc-badge-${v.vehicle_number}`}>{fmtDate(v.puc_expiry)}</span>;
}

function ShareDialog({ v, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { if (v) { setInfo(null); api.get(`/vehicles/${v.id}/share`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); } }, [v]);
  if (!v) return null;
  const docCount = DOCS.filter(([f]) => v[f]).length;
  const msg = info && `Vehicle Documents — ${v.vehicle_number} (${v.vehicle_type})\nDriver: ${info.driver ? `${info.driver.name} (${info.driver.phone})` : "Not assigned"}\nRC / Insurance / Fitness / PUC / Permit (${docCount} documents):\n${info.url}`;
  const download = async (fmt) => {
    try {
      const r = await api.get(`/vehicles/${v.id}/bundle`, { params: { fmt }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${v.vehicle_number}_Documents.${fmt}`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!v} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card" data-testid="share-dialog">
        <DialogHeader><DialogTitle>Share All Documents — {v.vehicle_number}</DialogTitle></DialogHeader>
        {!info ? <p className="text-sm text-muted-foreground">Preparing secure link…</p> : (
          <div className="space-y-3">
            <div className="rounded-lg border bg-secondary/50 p-3 text-xs" data-testid="share-message-preview">
              <div className="whitespace-pre-line">{msg}</div>
            </div>
            <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
              <MessageCircle className="h-4 w-4" /> Share via WhatsApp
            </a>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" data-testid="share-copy-btn" onClick={() => { navigator.clipboard.writeText(info.url); toast.success("Link copied"); }}><Link2 className="mr-1 h-4 w-4" />Copy Link</Button>
              <Button variant="outline" data-testid="share-zip-btn" onClick={() => download("zip")}><Download className="mr-1 h-4 w-4" />ZIP</Button>
              <Button variant="outline" data-testid="share-pdf-btn" onClick={() => download("pdf")}><Download className="mr-1 h-4 w-4" />PDF</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">The link opens a secure read-only page with all uploaded documents — no login needed. Only people with this link can view them.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocDetails({ field, expiryField, cur, onSave }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  if (!open) return <button type="button" title="Edit expiry" data-testid={`doc-edit-${field}`} onClick={() => { setF({ [expiryField]: cur[expiryField] || "" }); setOpen(true); }} className="text-xs font-semibold text-primary hover:underline">Edit expiry</button>;
  return (
    <div className="flex w-full flex-wrap items-end gap-2" data-testid={`doc-edit-form-${field}`}>
      <Field label="Expiry"><Input type="date" className="h-8 w-40" data-testid={`doc-expiry-${field}`} value={f[expiryField]} onChange={(e) => setF({ ...f, [expiryField]: e.target.value })} /></Field>
      <Button size="sm" data-testid={`doc-save-${field}`} onClick={async () => { if (await onSave(f)) setOpen(false); }}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
}

function VehicleDetailDialog({ v, onClose, onChanged, isAdmin, canEdit }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [cur, setCur] = useState(v);
  useEffect(() => setCur(v), [v]);
  const [k, setK] = useState(0);
  if (!cur) return null;
  const saveDoc = async (patch, msg) => {
    try {
      const next = { ...cur, ...patch };
      await api.put(`/vehicles/${cur.id}`, next);
      setCur(next);
      setK((x) => x + 1);
      toast.success(msg);
      onChanged();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <Dialog open={!!v} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="vehicle-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3"><span className="font-mono">{cur.vehicle_number}</span><StatusBadge status={cur.status} /></DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          {[["Type", cur.vehicle_type], ["Capacity", `${cur.capacity_tons} T`], ["Tax Expiry", cur.rc_expiry || "-"], ["Insurance Expiry", cur.insurance_expiry || "-"]].map(([l, val]) => (
            <div key={l} className="rounded-lg bg-secondary/60 p-3"><div className="label-xs">{l}</div><div className="mt-0.5 font-medium">{val}</div></div>
          ))}
        </div>
        <Tabs defaultValue="documents" className="mt-2">
          <TabsList>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory">Equipment & Tools Inventory</TabsTrigger>
          </TabsList>
          <TabsContent value="documents">
            <Button data-testid="share-all-docs-btn" onClick={() => setShareOpen(true)} className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700">
              <Share2 className="mr-2 h-4 w-4" /> Share All Documents
            </Button>
            <div className="mt-4 space-y-3">
              {DOCS.map(([field, expiryField, label]) => {
                const has = !!cur[field];
                return (
                  <div key={field} data-testid={`detail-doc-${field}`} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{label}</div>
                      <div className="text-xs text-muted-foreground">
                        {expiryField === "rc_expiry" ? "Tax Expiry" : "Expiry"}: {expiryField === "puc_expiry" ? <PucBadge v={cur} /> : <Expiry date={cur[expiryField]} />}
                        {" · "}{has ? "Document uploaded" : <span className="text-amber-600 font-medium">Not uploaded</span>}
                      </div>
                    </div>
                    <DocUpload label="" refType="vehicle_doc" value={cur[field]} disabled={!canEdit} testid={`doc-upload-${field}`} onChange={(id) => saveDoc({ [field]: id }, id ? "Document saved" : "Document removed")} />
                    {canEdit && <DocDetails field={field} expiryField={expiryField} cur={cur} onSave={(p) => saveDoc(p, "Details updated")} />}
                  </div>
                );
              })}
            </div>
            <DocHistory entity="vehicle" refId={cur.id} reloadKey={k} />
          </TabsContent>
          <TabsContent value="inventory">
            <InventoryTab vehicle={cur} canEdit={isAdmin} />
          </TabsContent>
        </Tabs>
        <ShareDialog v={shareOpen ? cur : null} onClose={() => setShareOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function VehicleDialog({ open, onClose, initial, onSaved, managerOnly }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => setF(initial || EMPTY), [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { ...f, capacity_tons: Number(f.capacity_tons), expense_model: f.expense_model || "actual", per_km_rate: f.expense_model === "per_km" ? Number(f.per_km_rate) || 0 : 0 };
      if (initial?.id) await api.put(`/vehicles/${initial.id}`, body);
      else await api.post("/vehicles", body);
      toast.success("Vehicle saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Vehicle Number"><Input data-testid="vehicle-number-input" disabled={managerOnly} value={f.vehicle_number} onChange={set("vehicle_number")} placeholder="TN-01-AB-1234" /></Field>
          <Field label="Type"><Input data-testid="vehicle-type-input" disabled={managerOnly} value={f.vehicle_type} onChange={set("vehicle_type")} placeholder="32 Ft MXL" /></Field>
          <Field label="Capacity (Tons)"><Input data-testid="vehicle-capacity-input" disabled={managerOnly} type="number" value={f.capacity_tons} onChange={set("capacity_tons")} /></Field>
          <Field label="FASTag ID / Tag Serial No."><Input data-testid="vehicle-fastag-input" disabled={managerOnly} value={f.fastag_id || ""} onChange={set("fastag_id")} placeholder="34161FA8203…" /></Field>
          <Field label="Expense Model Type"><select data-testid="vehicle-expense-model" disabled={managerOnly} className="native-select w-full" value={f.expense_model || "actual"} onChange={set("expense_model")}>
            <option value="actual">Actual Expenses (Standard)</option><option value="per_km">Fixed Per-KM Rate</option></select></Field>
          {f.expense_model === "per_km" && <Field label="Fixed Per-KM Rate (₹/km) *"><Input data-testid="vehicle-per-km-rate" disabled={managerOnly} type="number" min="0" step="0.01" value={f.per_km_rate || ""} onChange={set("per_km_rate")} /></Field>}
          <Field label="Tax Expiry"><Input data-testid="vehicle-rc-expiry-input" type="date" value={f.rc_expiry} onChange={set("rc_expiry")} /></Field>
          <Field label="Insurance Expiry"><Input data-testid="vehicle-insurance-input" type="date" value={f.insurance_expiry} onChange={set("insurance_expiry")} /></Field>
          <Field label="Fitness Expiry"><Input data-testid="vehicle-fitness-input" type="date" value={f.fitness_expiry} onChange={set("fitness_expiry")} /></Field>
          <Field label="PUC Expiry"><Input data-testid="vehicle-puc-expiry-input" type="date" value={f.puc_expiry} onChange={set("puc_expiry")} /></Field>
          <Field label="Permit Expiry"><Input data-testid="vehicle-permit-input" type="date" value={f.permit_expiry} onChange={set("permit_expiry")} /></Field>
          <Field label="Status">
            <select data-testid="vehicle-status-select" className="native-select" value={f.status} onChange={set("status")}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        {(
          <div className="mt-4 rounded-lg border p-4">
            <div className="label-xs mb-3">Vehicle Documents (PDF / JPG / PNG)</div>
            <div className="grid grid-cols-2 gap-3">
              {DOCS.map(([field, , label]) => (
                <FilePick key={field} label={label} required={false} refType="vehicle_doc" testid={`vehicle-doc-${field}`}
                  value={f[field]} onChange={(id) => setF({ ...f, [field]: id })} />
              ))}
            </div>
          </div>
        )}
        <DialogFooter><Button data-testid="vehicle-save-btn" onClick={save}>Save Vehicle</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Vehicles() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const [profit, setProfit] = useState(null);
  const [view, setView] = useState("list");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/vehicles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => items.filter((v) => (!status || v.status === status) && `${v.vehicle_number} ${v.vehicle_type}`.toLowerCase().includes(q.toLowerCase())), [items, q, status]);
  const del = async (v) => {
    if (!window.confirm(`Delete ${v.vehicle_number}?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = STATUSES.map((s) => [s, items.filter((v) => v.status === s).length]);

  return (
    <div>
      <PageHeader title="Fleet" subtitle={`${items.length} vehicles · ${counts.map(([s, n]) => `${n} ${s.toLowerCase()}`).join(" · ")}`}>
        {isAdmin && <div className="flex rounded-lg border p-0.5" data-testid="fleet-view-toggle">{[["list", "Vehicles"], ["ranking", "Profit Ranking"]].map(([k, l]) => <button key={k} data-testid={`fleet-view-${k}`} onClick={() => setView(k)} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}</div>}
        {["admin", "manager", "operation"].includes(role) && <Button data-testid="add-vehicle-btn" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" />Add Vehicle</Button>}
      </PageHeader>
      {view === "ranking" ? <FleetRanking onOpen={setProfit} /> : (<>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <SearchBox testid="vehicle-search-input" value={q} onChange={setQ} placeholder="Search number, type…" />
        <select data-testid="vehicle-status-filter" className="native-select sm:w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <TableWrap>
        <thead><tr>{["Vehicle", "Type", "Capacity", "Tax", "Insurance", "Fitness", "PUC", "Permit", "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.id} className={tr} data-testid={`vehicle-row-${v.vehicle_number}`}>
              <td className={`${td} font-mono font-semibold`}>{v.vehicle_number}</td>
              <td className={td}>{v.vehicle_type}</td>
              <td className={`${td} amount`}>{v.capacity_tons} T</td>
              <td className={td}><Expiry date={v.rc_expiry} /></td>
              <td className={td}><Expiry date={v.insurance_expiry} /></td>
              <td className={td}><Expiry date={v.fitness_expiry} /></td>
              <td className={td}><PucBadge v={v} /></td>
              <td className={td}><Expiry date={v.permit_expiry} /></td>
              <td className={td}><StatusBadge status={v.status} testid={`vehicle-status-${v.vehicle_number}`} /></td>
              <td className={`${td} text-right`}>
                {isAdmin && <button data-testid={`profit-vehicle-${v.vehicle_number}`} onClick={() => setProfit(v)} className="rounded p-1.5 text-muted-foreground hover:bg-emerald-50 hover:text-emerald-600" title="Profit card"><TrendingUp className="h-4 w-4" /></button>}
                <button data-testid={`view-vehicle-${v.vehicle_number}`} onClick={() => setDetail(v)} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Details & documents"><Eye className="h-4 w-4" /></button>
                {role !== "operation" && <button data-testid={`edit-vehicle-${v.vehicle_number}`} onClick={() => { setEdit(v); setOpen(true); }} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><Pencil className="h-4 w-4" /></button>}
                {isAdmin && <button data-testid={`delete-vehicle-${v.vehicle_number}`} onClick={() => del(v)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {rows.length === 0 && <Empty testid="vehicles-empty" />}
      </>)}
      <VehicleDialog open={open} onClose={() => setOpen(false)} initial={edit} onSaved={load} managerOnly={!isAdmin && !!edit} />
      <VehicleDetailDialog v={detail} onClose={() => setDetail(null)} onChanged={load} isAdmin={isAdmin} canEdit={["admin", "manager", "operation"].includes(role)} />
      <VehicleProfitDialog v={profit} onClose={() => setProfit(null)} />
    </div>
  );
}
